/**
 * Recognition certificates.
 */

import type {
  Certificate,
  CertificateActivity,
  CertificateIssueResult,
  CertificateListItem,
  VolunteerCertificateSummary,
} from '@parinaam-vms/shared';
import { apiGet, apiGetBlob, apiGetPage, apiPost, apiPostBlob, toQueryString } from './client';
import type { Page } from './client';

/** A `type` rather than an `interface`, so it can be passed to `toQueryString`. */
export type CertificateSummaryQuery = {
  search?: string;
  issued?: 'ISSUED' | 'NEVER';
  page?: number;
  page_size?: number;
};

/** Who a certificate could be issued to, and what it would say today. */
export function fetchCertificateSummaries(
  params: CertificateSummaryQuery
): Promise<Page<VolunteerCertificateSummary>> {
  return apiGetPage<VolunteerCertificateSummary>(
    `/certificates/volunteers${toQueryString(params)}`
  );
}

/** One volunteer's approved work, itemised by activity. */
export function fetchVolunteerActivities(volunteerId: string): Promise<CertificateActivity[]> {
  return apiGet<CertificateActivity[]>(`/certificates/volunteers/${volunteerId}/activities`);
}

/**
 * Issue to one or more volunteers.
 *
 * Always a list, even from the per-row button — see the backend validator.
 * Partial success is normal, so the caller has to read `outcomes` rather than
 * assume the whole request worked.
 */
export function issueCertificates(volunteerIds: string[]): Promise<CertificateIssueResult> {
  return apiPost<CertificateIssueResult>('/certificates', { volunteer_ids: volunteerIds });
}

/**
 * Issue a certificate in the admin's own words, to one volunteer.
 *
 * Always exactly one — see the backend validator. Unlike `issueCertificates`,
 * this always either succeeds or throws, so the caller does not read an
 * `outcomes` array.
 */
export function issueCustomCertificate(volunteerId: string, customText: string): Promise<Certificate> {
  return apiPost<Certificate>('/certificates/custom', {
    volunteer_id: volunteerId,
    custom_text: customText,
  });
}

/** The issue log, newest first. */
export function fetchCertificates(params: {
  volunteer_id?: string;
  page?: number;
  page_size?: number;
}): Promise<Page<CertificateListItem>> {
  return apiGetPage<CertificateListItem>(`/certificates${toQueryString(params)}`);
}

/** The document. Not an envelope — see `apiGetBlob`. */
export function fetchCertificatePdf(certificateId: string): Promise<Blob> {
  return apiGetBlob(`/certificates/${certificateId}/pdf`);
}

/**
 * The document as it would read if issued now — never stored, so there is no
 * id to fetch it by afterwards.
 */
export function fetchStandardPreviewPdf(volunteerId: string): Promise<Blob> {
  return apiGetBlob(`/certificates/volunteers/${volunteerId}/preview`);
}

/** The same, for wording typed but not yet committed to. */
export function fetchCustomPreviewPdf(volunteerId: string, customText: string): Promise<Blob> {
  return apiPostBlob('/certificates/custom/preview', {
    volunteer_id: volunteerId,
    custom_text: customText,
  });
}
